import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FlaskConical } from 'lucide-react';
import { BypassLayout } from '../components/common';
import V2DateTime from '../components/common/V2DateTime';
import PanelCloseButton from '../components/common/PanelCloseButton';
import { useAuth } from '../context/AuthContext';
import { AI_RUN_ROLES, BANK_APPROVE_ROLES, FIGURE_EDIT_ROLES, OCR_ROLES } from '../utils/roles';
import ProblemSearchPanel from '../components/ProblemHubV2/ProblemSearchPanel';
import { deriveNeeds } from '../components/ProblemHubV2/problemApi';
import ProblemIdLink, { ProblemNavGuard } from '../components/ProblemHubV2/ProblemIdLink';
import ProblemViewTab from '../components/ProblemHubV2/ProblemViewTab';
import AnalysisReviewCard from '../components/ProblemHubV2/AnalysisReviewCard';
import ContentHistoryTab from '../components/ProblemHubV2/ContentHistoryTab';
import VariationTab from '../components/ProblemHubV2/VariationTab';
import PaperUsageTab from '../components/ProblemHubV2/PaperUsageTab';
import SolversTab from '../components/ProblemHubV2/SolversTab';
import GenerateProblemModal from '../components/ProblemHubV2/GenerateProblemModal';
import AnalysisJobsModal from '../components/ProblemHubV2/AnalysisJobsModal';
import SingleOcrModal from '../components/ProblemHubV2/SingleOcrModal';
// 두 칸의 왼쪽(문제 보기) **기본** 폭 — 사용자가 경계를 옮기지 않았을 때 강의 작업 칸의 결과 칸과
// 같은 값으로 시작한다. 이 페이지에서는 아래 리사이저가 이 값을 로컬 환경 설정으로 덮을 수 있다.
import { VIEW_PANE_PX } from '../components/ProblemHubV2/viewPaneWidth';

// 문제 지면 미리보기(Lexical→HTML+KaTeX) — 목록의 평문 요약으로는 문제를 알아볼 수 없다
// v2 전용 미리보기 — 이 화면의 나머지(problemApi·검색·상세)가 전부 v2 라, 구형을 쓰면
// 한 화면 안에서 v1 과 v2 가 섞인다. 구형 파일은 손대지 않고 호출부만 옮긴다(오너 지시 2026-08-26).
const ProblemPreviewV2 = lazy(() => import('../components/ExamPapersV2/ProblemPreviewV2'));

/**
 * 「문제」 메뉴 (/problem-hub) — 레거시 문제관리를 대체하는 메인 문제 페이지.
 * 계약: BE-20260825-problem-hub-integrated.md (6축)
 *
 * 좌(마스터): [문제] 범용 검색·목록(①②, AI 모아보기 포함)
 * 우(디테일): 폭이 되면 **두 칸** — 왼쪽 [문제 보기](③, 인쇄 폭 고정) · 오른쪽 탭
 *   [수정 이력][분석](④)[변형](⑤)[시험지][학생]. 폭이 모자라면 한 단으로 돌아가 [문제 보기]가
 *   탭 줄의 첫 칸이 된다(설계 docs/problem-hub-v2/0911_SPEC_problem-detail-split.html).
 *   [AI 생성](⑥)은 목록 머리에서 연다. 탭은 주소(`?tab=`)가 쥔다 — 뒤로가기·앞으로가기·새로고침이 먹는다.
 *
 * 헤더·사이드바는 페이지가 스스로 두른다(BypassLayout) — 이 저장소 관례.
 * 마스터-디테일 치수는 형제 페이지(OcrHub·StudentsHub)와 같은 규칙을 쓴다.
 */

/** [문제 보기] — 두 칸에서는 왼쪽에 늘 서서 탭이 없고, 한 단(좁은 폭)에서만 탭 줄의 첫 칸이 된다 */
const VIEW_TAB = ['view', '문제 보기'];

/** 오른쪽 칸의 탭(한 단에서는 [문제 보기] 뒤에 붙는다) */
const SIDE_TABS = [
  ['history', '수정 이력'],
  ['analysis', '분석'],
  ['variation', '변형'],
  ['papers', '시험지'],
  // 이 문제를 받은 학생. 채점 전 학생도 든다(회신 §0) — 그래서 「푼 학생」이 아니라 「학생」이다(오너 결정 2026-09-11).
  // 교사도 본다: 서버가 담당 학생만 돌려준다(게이트는 상세와 같은 staff_required).
  ['students', '학생'],
];

/**
 * 문제를 처음 열 때 오른쪽 칸의 탭 — [학생](오너 결정 2026-09-11, 설계 문서 D2).
 * 한 번 고른 탭은 다른 문제로 옮겨도 유지된다. 한 단의 첫 화면은 이 값과 무관하게 [문제 보기]다.
 * 교사에게도 서는 탭이라 역할에 따라 첫 화면이 갈리지 않는다.
 */
const DEFAULT_SIDE_TAB = 'students';

/**
 * 두 칸의 치수 — 전부 CSS px. 근거는 설계 문서 §3·§4.
 *
 * 왼쪽 칸 기본 폭(VIEW_PANE_PX 488)은 viewPaneWidth.js 가 정본이다. 처음에는 본문 글자 폭이 미리보기와
 * 같은 자리에서 꺾이고, 사용자가 경계를 끌면 왼쪽 본문과 오른쪽 탭이 함께 늘고 줄어든다.
 *
 *   340 오른쪽 칸 최소 폭 — 탭 다섯과 닫기가 한 줄에 드는 폭(탭 줄 323 + 선 1 + 여백 16, 2026-09-11 실측).
 *       이보다 좁으면 탭 줄이 가로로 굴러 끝의 [학생]이 가려진다.
 *   16  문턱의 여유 — 스크롤바가 생기고 사라지며 폭이 15px 흔들릴 때 두 모드를 오가지 않게.
 */
const SIDE_MIN_PX = 340;
const VIEW_MIN_PX = 340;
const SPLIT_ENTER_PX = VIEW_PANE_PX + SIDE_MIN_PX; // 828 이상이면 두 칸
const SPLIT_LEAVE_PX = SPLIT_ENTER_PX - 16; // 812 미만이면 한 단 — 그 사이는 직전 모드를 지킨다
const VIEW_PANE_STORAGE_KEY = 'ky.problemHub.viewPaneWidthPx';
const VIEW_PANE_KEY_STEP_PX = 24;
/** 왼쪽 칸 — 머리 줄의 왼쪽 칸도 **같은 클래스**를 써서, 탭 줄이 오른쪽 칸의 시작선에 선다(스크롤바 자리까지 같게) */
const VIEW_COL = 'shrink-0 pr-4 [scrollbar-gutter:stable]';

/** 저장값·드래그값을 지금 상세 몸통 안에서 가능한 폭으로 제한한다. 오른쪽 칸 최소 폭은 항상 남긴다. */
function clampViewPaneWidth(value, totalWidth) {
  const n = Number(value);
  const wanted = Number.isFinite(n) && n > 0 ? n : VIEW_PANE_PX;
  const usable = Number.isFinite(totalWidth) && totalWidth > 0 ? totalWidth : SPLIT_ENTER_PX;
  const max = Math.max(VIEW_MIN_PX, usable - SIDE_MIN_PX);
  return Math.round(Math.min(max, Math.max(VIEW_MIN_PX, wanted)));
}

function readViewPaneWidth() {
  if (typeof window === 'undefined') return VIEW_PANE_PX;
  try {
    const raw = window.localStorage.getItem(VIEW_PANE_STORAGE_KEY);
    if (raw == null || raw.trim() === '') return VIEW_PANE_PX;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n : VIEW_PANE_PX;
  } catch (_) {
    return VIEW_PANE_PX; // 환경 설정을 못 읽어도 화면 자체는 기본 폭으로 연다
  }
}

function writeViewPaneWidth(value) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VIEW_PANE_STORAGE_KEY, String(Math.round(value)));
  } catch (_) {
    // 브라우저가 저장을 막아도 현재 세션의 드래그는 그대로 쓸 수 있다
  }
}

/**
 * 상세 몸통의 폭을 재서 두 칸인지 정한다. 칸 폭은 화면 폭·사이드바·목록이 함께 정해 미디어쿼리로는 못 잰다.
 *
 * 돌려주는 ref 는 **콜백 ref** 다 — 상세는 문제를 고를 때만 서고, 모바일·데스크톱이 서로 다른 부모에
 * 세우므로 붙는 요소가 바뀔 때마다 다시 관찰해야 한다.
 * 첫 페인트 전에 잰다(useLayoutEffect) — 한 단으로 그렸다가 두 칸으로 튀는 깜빡임을 막는다
 * (ProblemBodyPreview 의 usePaperFit 과 같은 이유).
 * ResizeObserver 가 없거나 폭이 0 이면 한 단이다 — 검사 환경(jsdom)이 여기 해당해 한 단 검사가 그대로 돈다.
 */
function useSplitLayout() {
  const [el, setEl] = useState(null);
  const [twoCol, setTwoCol] = useState(false);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    if (el == null) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      setWidth(w);
      setTwoCol((prev) => (prev ? w >= SPLIT_LEAVE_PX : w >= SPLIT_ENTER_PX));
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [el]);
  return [setEl, twoCol, width];
}

function ProblemHub() {
  const { problemId, workId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  /**
   * 권한 판정은 **이 한 곳에서만** 읽고 아래로 내린다(src/utils/roles.js 가 정본).
   *
   * 부품 안에서 useAuth 로 읽지 않는 이유 둘 —
   * ① 같은 부품이 다른 화면에도 실린다(VariationReviewCard 는 배치 화면에도 있다). 부품이 스스로
   *    판정하면 화면마다 다른 규칙을 줄 길이 없어진다.
   * ② 부품이 읽으면 prop 을 안 넘기는 시험·마운트 지점이 **조용히 「감춤」으로 떨어진다** —
   *    검사하던 버튼이 사라졌는데 시험은 통과한다. 그래서 아래 부품들의 boolean 기본값은 허용(true)이다.
   */
  const { userType } = useAuth();
  const canApprove = BANK_APPROVE_ROLES.includes(userType); // 승인·폐기·본문 되돌리기 — 서버가 교사에게 403
  const canRunAi = AI_RUN_ROLES.includes(userType);         // LLM 비용이 나는 실행 — 서버는 열려 있고 화면만 감춘다
  const canEditFigures = FIGURE_EDIT_ROLES.includes(userType); // 그림 위 버튼 넷 — 강사에게는 크기 조절만(오너 지시 2026-09-10)
  const canOpenOcr = OCR_ROLES.includes(userType);           // 「OCR 등록」 배지 → OCR 검수 모달 — 가는 곳이 매니저 전용(오너 지시 2026-10-02)

  // 구 /problem-hub/work/:workId 딥링크 — 재작성으로 work 상세가 없어졌다. 목록으로 돌린다.
  useEffect(() => {
    if (workId != null) navigate('/problem-hub', { replace: true });
  }, [workId, navigate]);

  const [splitRef, twoCol, splitWidth] = useSplitLayout();
  const [preferredViewPanePx, setPreferredViewPanePx] = useState(readViewPaneWidth);
  const viewPaneMaxPx = Math.max(VIEW_MIN_PX, splitWidth - SIDE_MIN_PX);
  const viewPanePx = clampViewPaneWidth(preferredViewPanePx, splitWidth);
  const resizeCleanupRef = useRef(null);
  const splitWidthRef = useRef(splitWidth);
  splitWidthRef.current = splitWidth;

  /**
   * 문제 보기 ↔ 오른쪽 탭 경계. 드래그 중에는 화면 state 만 바꾸고, 손을 뗄 때 한 번만 localStorage 에
   * 쓴다 — pointermove 마다 동기 저장소를 두드리면 긴 표를 그리는 동안 커서가 끊긴다.
   */
  const beginPaneResize = useCallback((e) => {
    if (!twoCol || splitWidth <= 0 || (e.button != null && e.button !== 0)) return;
    e.preventDefault();
    resizeCleanupRef.current?.();

    const startX = e.clientX;
    const startWidth = viewPanePx;
    const pointerId = e.pointerId;
    let latest = startWidth;
    let active = true;
    const body = document.body;
    const previousCursor = body.style.cursor;
    const previousUserSelect = body.style.userSelect;
    body.style.cursor = 'col-resize';
    body.style.userSelect = 'none';

    const isOtherPointer = (event) => (
      pointerId != null && event?.pointerId != null && event.pointerId !== pointerId
    );
    const move = (event) => {
      if (!active || isOtherPointer(event)) return;
      event.preventDefault();
      // 창·사이드바 폭이 드래그 도중 바뀌어도 지금 실제 몸통 폭을 기준으로 양쪽 최소 폭을 지킨다.
      latest = clampViewPaneWidth(startWidth + event.clientX - startX, splitWidthRef.current);
      setPreferredViewPanePx(latest);
    };
    let finish;
    const cleanup = () => {
      if (!active) return;
      active = false;
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
      window.removeEventListener('blur', finish);
      body.style.cursor = previousCursor;
      body.style.userSelect = previousUserSelect;
      writeViewPaneWidth(latest);
      resizeCleanupRef.current = null;
    };
    finish = (event) => {
      // 두 번째 손가락·펜이 먼저 떨어져도 첫 포인터의 드래그는 끝내지 않는다. blur 는 포인터가 없어 그대로 끝낸다.
      if (isOtherPointer(event)) return;
      cleanup();
    };

    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    window.addEventListener('blur', finish);
    resizeCleanupRef.current = cleanup;
  }, [splitWidth, twoCol, viewPanePx]);

  const resizePaneWithKey = useCallback((e) => {
    if (!twoCol) return;
    let next = null;
    if (e.key === 'ArrowLeft') next = viewPanePx - VIEW_PANE_KEY_STEP_PX;
    else if (e.key === 'ArrowRight') next = viewPanePx + VIEW_PANE_KEY_STEP_PX;
    else if (e.key === 'Home') next = VIEW_MIN_PX;
    else if (e.key === 'End') next = viewPaneMaxPx;
    if (next == null) return;
    e.preventDefault();
    const clamped = clampViewPaneWidth(next, splitWidth);
    setPreferredViewPanePx(clamped);
    writeViewPaneWidth(clamped);
  }, [splitWidth, twoCol, viewPaneMaxPx, viewPanePx]);

  const resetPaneWidth = useCallback(() => {
    const next = clampViewPaneWidth(VIEW_PANE_PX, splitWidth);
    setPreferredViewPanePx(next);
    writeViewPaneWidth(next);
  }, [splitWidth]);

  // 드래그 중 폭이 줄어 한 단이 되면 손잡이가 사라진다. 그 순간 전역 리스너·body 커서도 함께 거둔다.
  useLayoutEffect(() => {
    if (!twoCol) resizeCleanupRef.current?.();
  }, [twoCol]);

  // 드래그 도중 페이지가 닫혀도 전역 리스너·body 커서를 남기지 않는다.
  useEffect(() => () => { resizeCleanupRef.current?.(); }, []);
  /**
   * 화면에 세울 오른쪽 탭 — [변형]은 교사에게 통째로 없앤다.
   *
   * 그 탭 안에 있는 것은 전부 막힌 것뿐이다: AI 실행(변형 만들기·다시 생성)과 승인·반려.
   * 남길 것이 하나도 없어 탭만 남기면 빈 껍데기가 되고, 화면이 「여기서 뭔가 할 수 있다」고 거짓을 말한다.
   *
   * SIDE_TABS 상수는 그대로 두고 **그리는 목록만** 거른다 — 원본을 지우면 이 배열이 정본이라는 사실이 흐려진다.
   */
  const visibleSideTabs = useMemo(
    () => SIDE_TABS.filter(([key]) => (key === 'variation' ? (canRunAi || canApprove) : true)),
    [canRunAi, canApprove],
  );
  /**
   * 지금 탭 — **주소(`?tab=`)가 정본이다**(오너 요구 2026-09-11 「뒤로가기/앞으로가기가 적용되도록」).
   * 시험지 상세(ExamPapers.js 의 tab 파생값·goTab)와 같은 규칙이다: 탭 버튼은 주소만 바꾸므로
   * 새로고침·뒤로가기·앞으로가기가 손댈 것 없이 먹고, 주소를 건네면 상대도 같은 탭을 본다.
   *
   * 주소의 값은 셋 중 하나다 — 없음(처음 연 상태) · 'view'(한 단의 [문제 보기]) · 보이는 오른쪽 탭.
   * 없음과 'view' 는 둘 다 「한 단이면 [문제 보기], 두 칸이면 오른쪽 칸은 처음 탭([학생])」이다.
   * **탭 기억을 state 로 두지 않는다** — 두면 뒤로가기로 처음 주소에 돌아와도 마지막에 본 탭이 남아
   * 주소와 화면이 갈린다(2026-09-11 실측: [분석]→[시험지]→뒤로 두 번에 [학생]이 아니라 [분석]이 섰다).
   * 그 기억이 필요하던 자리(새 문제로 옮기기)는 주소에 탭을 직접 달아 넘긴다(problemPath·openProblemFront).
   *
   * 밖에서 탭을 지정해 들어오는 길도 이 주소다 — 학습(/students-hub) 문제 카드의 [AI 변형]이
   * `?tab=variation` 으로 연다(ProblemCardsV2). 종전의 route state(`state.tab`)는 주소에 남지 않아
   * 뒤로가기로 돌아오면 탭이 사라졌다.
   * 모르는 값·감춘 탭(교사의 `?tab=variation`)은 없음으로 읽고, 아래 정규화 effect 가 주소에서 걷는다 —
   * 남이 준 값으로 없는 탭을 세우지 않는다.
   */
  const search = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const requestedTab = search.get('tab');
  const urlSide = visibleSideTabs.some(([key]) => key === requestedTab) ? requestedTab : null;
  const sideTab = urlSide ?? DEFAULT_SIDE_TAB;
  // 한 단에서 [문제 보기]를 보고 있는가 — 주소에 오른쪽 탭이 없으면 참. 두 칸에서는 읽지 않는다.
  const narrowView = urlSide == null;
  /** 지금 고른 탭 — 한 단 탭 줄의 선택(두 칸의 오른쪽 칸은 sideTab 을 본다) */
  const currentTab = narrowView ? VIEW_TAB[0] : sideTab;
  useEffect(() => {
    if (requestedTab == null || requestedTab === VIEW_TAB[0] || urlSide) return;
    const next = new URLSearchParams(location.search);
    next.delete('tab');
    const q = next.toString();
    navigate({ pathname: location.pathname, search: q ? `?${q}` : '' }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도: 주소의 탭 값이 바뀔 때만 거른다
  }, [requestedTab, urlSide]);
  const sideShown = twoCol || !narrowView;
  // 오른쪽 탭은 **보이게 된 것만** 마운트해 두고 hidden 으로 감춘다 — 매 전환마다 다시 읽고 Lexical 에디터를
  // 새로 세우면 탭을 오갈 때마다 깜빡인다. 문제를 바꾸면 보이는 탭만 남긴다.
  // 한 단에서 [문제 보기]를 보는 동안에는 세우지 않는다 — 누르기 전에 부르지 않는다([학생]이 처음 탭이어도
  // 한 단 첫 화면에서는 학생 목록을 읽지 않는다).
  // [문제 보기]는 이 목록에 없다 — **늘 마운트한다.** 두 칸에서 늘 보이고, 한 단↔두 단을 오갈 때
  // 언마운트되면 미저장 편집이 사라진다(Lexical 은 언마운트하며 내용을 비운다 — ProblemViewTab 머리주석).
  const [mounted, setMounted] = useState({});
  const [genOpen, setGenOpen] = useState(false); // ⑥ AI 문제 생성 — 문제 선택과 무관한 별도 진입
  // 분석 작업 목록 — 태운 분석을 모아 보고 실패한 것을 다시 태운다(BE-20260829-reply-fe-0828.md §1-2).
  // 문제 선택과 무관한 별도 진입이라 [AI 문제 생성]과 같은 자리(목록 머리)에서 연다.
  const [jobsOpen, setJobsOpen] = useState(false);
  const [singleOcrOpen, setSingleOcrOpen] = useState(false);
  const [previewIds, setPreviewIds] = useState(null); // 목록에서 연 문제 미리보기
  // 머리의 문제 번호 색과 수정 시각은 [문제 보기]가 읽은 상세에서 가져온다.
  // need_* 는 상세에서 deriveNeeds 로 파생한다(problemApi.deriveNeeds).
  // [문제 보기]는 늘 마운트되므로 문제를 열면 곧 채워진다. 받기 전(읽는 중·실패)에는 null 이고,
  // 그때 번호는 색 없이 먹색이다 — 모르는 것을 아는 척하지 않는다.
  const [detailHeader, setDetailHeader] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도: 문제가 바뀔 때만, 그 시점에 보이는 탭만 남긴다
  useEffect(() => { setMounted(sideShown ? { [sideTab]: true } : {}); }, [problemId]);
  useEffect(() => {
    if (sideShown) setMounted((m) => (m[sideTab] ? m : { ...m, [sideTab]: true }));
  }, [sideTab, sideShown]);
  // 승인·되돌리기 등으로 문제은행 값이 바뀌면 조회 탭과 목록을 함께 갱신한다
  const [refreshKey, setRefreshKey] = useState(0);
  const bumpRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const selectedId = problemId ?? null;
  const shownDetail = detailHeader && String(detailHeader.problemId) === String(selectedId) ? detailHeader : null;
  const updatedAt = shownDetail?.updatedAt;
  // 검수 카드의 미저장 편집 — 문제를 옮기거나 닫으면 카드가 언마운트되며 사라진다.
  // OcrHub 검수 모달과 같은 규칙으로 이동 전에 한 번 묻는다.
  //
  // ⚠ 출처별로 나눠 담는다. 방문한 탭은 모두 마운트된 채 hidden 으로만 감춰지므로 분석 카드와
  // 변형 카드가 동시에 살아 있다. 플래그가 하나면 나중에 마운트한 쪽이 보내는 최초의 false 가
  // 앞선 편집의 표시를 지워, 닫아도 아무도 묻지 않는다.
  const dirtyMapRef = useRef({});
  const markDirty = useCallback((source) => (d) => { dirtyMapRef.current[source] = d; }, []);
  const anyDirty = () => Object.values(dirtyMapRef.current).some(Boolean);
  // 플래그를 여기서 내리지 않는다 — 확인만 받는다. 실제로 언마운트되지 않는 경로(이미 열린
  // 문제를 다시 클릭)에서 내려 버리면 편집은 살아 있는데 가드만 풀린다. 해제는 카드가
  // 언마운트되며 스스로 알린다.
  // 참조를 고정한다 — 이 함수는 화면 전체에 <ProblemNavGuard> 로 내려가고, 매 렌더마다
  // 새 함수면 그것을 읽는 문제 번호 링크 전부(한 페이지에 스무 개)가 함께 다시 그려진다.
  // ref 만 읽으므로 첫 렌더의 것을 그대로 써도 값은 늘 최신이다.
  const confirmDiscard = useCallback(() => {
    if (!Object.values(dirtyMapRef.current).some(Boolean)) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('저장하지 않은 변경 사항이 있습니다. 버리고 이동할까요?');
  }, []);
  useEffect(() => {
    const onBeforeUnload = (e) => { if (anyDirty()) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, []);
  /** 문제 상세 주소 — 탭을 달고 간다. 기본은 지금 탭이다(한 번 고른 탭은 다른 문제로 옮겨도 유지된다 — 설계 D2) */
  const problemPath = (id, tabKey = requestedTab) => `/problem-hub/problem/${id}${tabKey ? `?tab=${encodeURIComponent(tabKey)}` : ''}`;
  const select = (id) => { if (confirmDiscard()) navigate(problemPath(id)); };
  const closeDetail = () => { if (confirmDiscard()) navigate('/problem-hub'); };
  /**
   * 탭을 옮긴다 — **주소를 바꾸는 것이 곧 탭을 바꾸는 것**이다(위 파생값). 시험지 상세의 goTab 과 같은 규칙:
   * push 로 쌓아 뒤로가기로 직전 탭에 돌아온다. 미저장 가드를 지나지 않는다 — 탭은 패널을 언마운트하지 않고
   * hidden 으로 숨길 뿐이라 버릴 편집이 없다. 같은 탭을 다시 누르면 아무것도 하지 않는다 — 같은 주소가
   * 겹쳐 쌓이면 뒤로가기를 두 번 눌러야 직전 탭에 닿는다.
   * 두 칸에서는 오른쪽 칸이 이미 보이는 탭(sideTab)과 견준다 — 처음 연 상태(주소에 탭 없음)에서 [학생]을 누르면 그대로다.
   */
  const goTab = (key) => {
    if (selectedId == null || key === (twoCol ? sideTab : currentTab)) return;
    const next = new URLSearchParams(location.search);
    next.set('tab', key);
    navigate(`${location.pathname}?${next.toString()}`);
  };
  /**
   * 새로 만든 문제로 옮기며 본문을 앞에 세운다 — 한 단이면 [문제 보기]로, 두 칸이면 오른쪽 탭은 지금 그대로
   * (변형 승인 뒤라면 [변형]에 남아 나머지 후보를 이어서 본다). 두 칸은 왼쪽에 본문이 늘 서 있다.
   */
  const openProblemFront = (id) => navigate(problemPath(id, twoCol ? urlSide : VIEW_TAB[0]));

  // 두 패널을 항상 마운트하고 hidden 으로만 감춘다 — 조건부 렌더면 모드를 오갈 때마다,
  // 모바일에서는 문제를 열 때마다 검색 조건·페이지·스크롤이 통째로 사라진다(8개 필터가 얹힌
  // 패널이라 형제 페이지의 단순 목록과 손실 규모가 다르다).
  const renderList = (isMobileView) => (
    <div className="flex h-full min-h-0 flex-col">
      {/* [분석 대상] 패널은 은퇴했다 — 같은 확정문제 더미를 필터만 빼고 다시 보여 주던 것이라
          화면이 똑같아 보였다. 이제 '분석 필요만 보기'(needs_analysis) 체크박스 하나로 갈린다. */}
      <div className="min-h-0 flex-1">
        {/* [AI 문제 생성]은 패널 제목 행에 산다 — 버튼 하나가 목록 위 한 줄을 통째로 쓰고 있었고,
            결과가 쌓이는 곳이 이 목록이라 목록의 머리가 제자리다. 모달은 여기서 연다. */}
        <ProblemSearchPanel
          selectedId={selectedId}
          onSelect={select}
          reloadKey={refreshKey}
          layout={!isMobileView && selectedId == null ? 'table' : 'card'}
          onPreview={(it) => setPreviewIds([it.problem_id])}
          /* AI 실행 진입 둘은 **콜백을 안 넘기면 서지 않는** 구조라 부품을 손대지 않고 여기서 가른다
             (ExamPapers 의 onAiCopy 와 같은 패턴). 행마다 붙는 [분석]은 패널이 제 상태로 여는 창이라
             콜백으로 못 가르고, 그것만 boolean 으로 내린다. */
          onGenerate={canRunAi ? () => setGenOpen(true) : null}
          onOpenAnalysisJobs={canRunAi ? () => setJobsOpen(true) : null}
          onSingleOcr={canOpenOcr ? () => setSingleOcrOpen(true) : null}
          canRunAi={canRunAi}
        />
      </div>
    </div>
  );

  // [문제 보기] 칸 머리의 [보기|편집] 자리 — 콜백 ref 로 받는다(요소가 생기고 사라질 때 ProblemViewTab 이 다시 그린다)
  const [modeSlot, setModeSlot] = useState(null);

  const renderDetail = () => {
    const idBadge = (
      <>
        <FlaskConical className="size-4 shrink-0 text-primary" />
        {/* 번호가 곧 상태다 — 목록의 표·카드와 **같은 부품**을 쓴다(ProblemIdLink).
            상세를 아직 못 받았으면(읽는 중·실패) item 을 주지 않아 색이 먹색으로 굳는다.
            **링크가 아니다** — 지금 보고 있는 것이 그 문제다. 제자리로 가는 길은 길이 아니다. */}
        <ProblemIdLink id={selectedId} item={shownDetail?.needs} linked={false} size="lg" className="shrink-0" />
      </>
    );
    const tabRow = (tabs, selectedKey, onPick) => (
      /* min-w-0 + 가로 스크롤 — overflow-hidden 만 두면 좁은 폭에서 마지막 탭이 잘려 아예 못 누른다 */
      <div className="flex min-w-0 overflow-x-auto rounded-full bg-gray-100 text-xs" role="tablist" aria-label="문제 상세 보기">
        {tabs.map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            id={`ph-tab-${key}`}
            aria-controls={`ph-panel-${key}`}
            aria-selected={selectedKey === key}
            onClick={() => onPick(key)}
            className={`whitespace-nowrap px-3 py-1 transition-colors ${
              selectedKey === key ? 'bg-primary font-medium text-white' : 'text-gray-500 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    );
    const closeButton = (
      <PanelCloseButton onClose={closeDetail} />
    );
    const sidePanel = (key) => (sideTab === key ? '' : 'hidden');

    return (
      <div className="flex h-full min-h-0 flex-col rounded-3xl bg-white p-4 shadow-lg">
        {/* 머리 줄은 상태를 들지 않아 모드마다 다르게 그려도 잃는 것이 없다.
            두 칸: 왼쪽 칸(번호)이 본문 칸과 같은 폭 클래스라 탭 줄이 오른쪽 칸의 시작선에 선다 —
            오른쪽 칸의 선(border-l) 1px 도 투명한 선으로 똑같이 두어 맞춘다. */}
        {twoCol ? (
          <div className="mb-3 flex shrink-0 items-stretch border-b pb-2">
            <div className={`${VIEW_COL} overflow-y-hidden`} style={{ width: viewPanePx }}>
              <div className="flex size-full items-center gap-2">
                {idBadge}
                <div className="min-w-0 flex-1 text-center">
                  {updatedAt && (
                    <V2DateTime
                      value={updatedAt}
                      format="dateTimeDot"
                      prefix="수정 "
                      aria-label="마지막 수정 시각"
                      className="block truncate whitespace-nowrap text-[10px] tabular-nums text-gray-400"
                    />
                  )}
                </div>
                {/* [보기|편집] 전환이 그려질 자리 — 본문 칸 오른쪽 가장자리와 같은 선(오너 지시 2026-09-13).
                    상태는 ProblemViewTab 이 쥐고 단추만 여기로 그려 넣는다(modeSlot · createPortal). */}
                <div ref={setModeSlot} className="flex shrink-0 items-center" />
              </div>
            </div>
            <div className="flex min-w-0 flex-1 items-center gap-2 border-l border-transparent">
              {tabRow(visibleSideTabs, sideTab, goTab)}
              {closeButton}
            </div>
          </div>
        ) : (
          <div className="mb-3 flex shrink-0 items-center gap-2 border-b pb-2">
            {idBadge}
            {tabRow([VIEW_TAB, ...visibleSideTabs], currentTab, goTab)}
            {closeButton}
          </div>
        )}
        {/* 몸통은 두 모드가 **같은 트리**를 쓴다 — 칸의 클래스만 바뀐다. 모드가 바뀔 때 [문제 보기]가
            다시 마운트되면 미저장 편집이 사라지기 때문이다. 스크롤은 칸마다 따로 — 오른쪽 탭을 넘겨도
            본문 위치가 그대로다. data-layout 은 지금 모드를 밖(검사·실측)에서 읽는 자리다. */}
        <div ref={splitRef} data-layout={twoCol ? 'split' : 'single'} className="flex min-h-0 flex-1">
          <div
            id="ph-panel-view"
            role={twoCol ? 'region' : 'tabpanel'}
            aria-label={twoCol ? VIEW_TAB[1] : undefined}
            aria-labelledby={twoCol ? undefined : `ph-tab-${VIEW_TAB[0]}`}
            className={twoCol
              ? `${VIEW_COL} min-h-0 overflow-auto`
              : `min-h-0 min-w-0 flex-1 overflow-y-auto ${narrowView ? '' : 'hidden'}`}
            style={twoCol ? { width: viewPanePx } : undefined}
          >
            {/* 기본 폭에서는 종전의 인쇄 기준 457px 이고, 경계를 끌면 이 내용 폭도 함께 변한다.
                한 단은 지금처럼 칸을 전부 쓴다. */}
            <div style={twoCol ? { width: '100%' } : undefined}>
              {/* [이미지 추가]는 AI 생성 갈래를 품고 있어(FigureAddModal) LLM 비용이 난다 — 강사에게는
                  버튼째 감춘다(오너 지시 2026-09-10). 2026-09-09 감추기 때 이 탭만 빠져 있었다.
                  그림 위 버튼 넷도 같은 날 통째로 닫았다(canEditFigures) — 축이 달라 상수를 따로 둔다(roles.js). */}
              <ProblemViewTab
                key={`v-${selectedId}`}
                problemId={selectedId}
                refreshKey={refreshKey}
                onOpenProblem={(pid) => { if (pid != null) select(pid); }}
                onLoaded={(d) => setDetailHeader(d ? {
                  problemId: d.problem_id, needs: deriveNeeds(d), updatedAt: d.updated_at,
                } : null)}
                onDirtyChange={markDirty('view')}
                onChanged={bumpRefresh}
                canRunAi={canRunAi}
                canEditFigures={canEditFigures}
                canOpenOcr={canOpenOcr}
                /* 두 칸에서만 머리 자리를 준다 — 한 단은 [문제 보기]가 탭 중 하나라 전환이 본문 줄에 남는다 */
                modeSlot={twoCol ? modeSlot : null}
              />
            </div>
          </div>
          {twoCol && (
            /* 폭을 차지하지 않는 경계 위에 12px 잡기 영역을 겹친다 — 종전 border 1px 위치와
               탭 시작선을 옮기지 않으면서 마우스·펜으로는 넉넉히 잡힌다. */
            <div
              role="separator"
              aria-label="문제 보기와 오른쪽 패널 너비 조절"
              aria-orientation="vertical"
              aria-valuemin={VIEW_MIN_PX}
              aria-valuemax={viewPaneMaxPx}
              aria-valuenow={viewPanePx}
              aria-valuetext={`문제 보기 ${viewPanePx}px, 오른쪽 패널 ${Math.max(0, splitWidth - viewPanePx)}px`}
              tabIndex={0}
              title="좌우로 드래그해 폭 조절 · 방향키로 이동 · 두 번 누르면 기본 폭"
              data-testid="problem-detail-resizer"
              onPointerDown={beginPaneResize}
              onKeyDown={resizePaneWithKey}
              onDoubleClick={resetPaneWidth}
              className="group relative z-20 w-0 shrink-0 cursor-col-resize outline-none"
            >
              <span
                aria-hidden="true"
                className="pointer-events-auto absolute inset-y-0 left-0 w-3 -translate-x-1/2 cursor-col-resize touch-none"
              >
                <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-gray-100 transition-colors group-hover:bg-primary group-focus-visible:bg-primary" />
              </span>
            </div>
          )}
          <div
            id="ph-side-panel"
            className={twoCol
              ? 'min-h-0 min-w-0 flex-1 overflow-y-auto border-l border-gray-100 pl-4'
              : `min-h-0 min-w-0 flex-1 overflow-y-auto ${narrowView ? 'hidden' : ''}`}
          >
            {mounted.analysis && (
              <div className={sidePanel('analysis')} role="tabpanel" id="ph-panel-analysis" aria-labelledby="ph-tab-analysis">
                {/* [저장](PATCH /analysis/{id})은 교사에게 열려 있다 — 함께 감추지 않는다.
                    이 탭에서 막힌 것은 승인·폐기(403)와 AI 요청(비용)뿐이다. */}
                <AnalysisReviewCard
                  key={`a-${selectedId}`}
                  problemId={selectedId}
                  canApprove={canApprove}
                  canRunAi={canRunAi}
                  onChanged={bumpRefresh}
                  onDirtyChange={markDirty('analysis')}
                  /* 이 패널도 숨김으로만 감춰진다 — 안 넘기면 문제 편집에서 개념·난이도를 바꿔도 「은행 현재값」이 옛 값이다(2026-09-13) */
                  reloadKey={refreshKey}
                />
              </div>
            )}
            {mounted.history && (
              <div className={sidePanel('history')} role="tabpanel" id="ph-panel-history" aria-labelledby="ph-tab-history">
                {/* reloadKey 를 반드시 넘긴다 — 이 패널은 탭을 옮겨도 언마운트되지 않고 hidden 으로만
                    감춰지므로, 안 넘기면 **마운트 때 받은 목록을 영원히 들고 있는다.** 저장해도 새 판이
                    안 보인다(오너 보고 2026-08-30 「방금 풀이를 수정했는데 수정 이력에 보이지 않음」). */}
                {/* 목록·판 비교·필터·새로고침은 전부 GET 이라 그대로 둔다 — 막힌 것은 되돌리기 하나다
                    (POST /problem-content/problems/{id}/revert). 시험지·그림 되돌리기는 교사에게 열려 있다. */}
                <ContentHistoryTab
                  key={`h-${selectedId}`}
                  problemId={selectedId}
                  reloadKey={refreshKey}
                  canRevert={canApprove}
                  onChanged={bumpRefresh}
                />
              </div>
            )}
            {mounted.variation && (canRunAi || canApprove) && (
              <div className={sidePanel('variation')} role="tabpanel" id="ph-panel-variation" aria-labelledby="ph-tab-variation">
                {/* 승인해도 자동으로 이동하지 않는다 — 한 원본에서 후보를 여러 개 뽑아 고르는 것이
                    이 탭의 쓰임인데(계약 §1), 첫 승인에 화면이 새 문제로 튀면 나머지 후보 검수가 끊기고
                    방금 만든 문제 번호를 알리는 토스트도 함께 사라진다. 이동은 카드의 [새 문제 보기]로.
                    이동하면 한 단에서는 [문제 보기]로 넘어가고, 두 칸에서는 왼쪽이 새 문제를 보이며
                    오른쪽은 [변형]에 남는다. */}
                <VariationTab
                  key={`x-${selectedId}`}
                  problemId={selectedId}
                  canRunAi={canRunAi}
                  canApprove={canApprove}
                  onApproved={() => bumpRefresh()}
                  onDirtyChange={markDirty('variation')}
                  onOpenProblem={(newId) => {
                    if (newId == null) return;
                    dirtyMapRef.current.variation = false; // 승인한 카드의 편집은 이미 저장됐다
                    openProblemFront(newId);
                  }}
                />
              </div>
            )}
            {mounted.papers && (
              <div className={sidePanel('papers')} role="tabpanel" id="ph-panel-papers" aria-labelledby="ph-tab-papers">
                {/* 이 탭만 **다른 페이지로 나간다** — 시험지를 열면 이 화면을 떠나므로 이동 전에
                    미저장 편집을 묻는다(다른 탭은 같은 페이지 안에서만 움직여 가드가 필요 없었다). */}
                <PaperUsageTab
                  key={`p-${selectedId}`}
                  problemId={selectedId}
                  onOpenPaper={(paperId) => {
                    if (paperId == null) return;
                    if (!confirmDiscard()) return;
                    navigate(`/exam-papers/paper/${paperId}`);
                  }}
                />
              </div>
            )}
            {mounted.students && (
              <div className={sidePanel('students')} role="tabpanel" id="ph-panel-students" aria-labelledby="ph-tab-students">
                {/* [시험지] 탭처럼 **다른 페이지로 나간다** — 행을 누르면 그 학생의 학습 화면으로 가므로 이동 전에
                    미저장 편집을 묻는다. 학습 화면은 번호만으로 학생을 따로 불러와 목록 밖에서 와도 열린다. */}
                <SolversTab
                  key={`s-${selectedId}`}
                  problemId={selectedId}
                  onOpenStudent={(studentId) => {
                    if (studentId == null) return;
                    if (!confirmDiscard()) return;
                    navigate(`/students-hub/student/${studentId}`);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  /* 문제 번호 링크가 이동 전에 물어볼 수 있게 가드를 내린다 — 검수 카드의 미저장 편집은
     이 페이지 어디서 눌러도 같은 규칙으로 지켜져야 한다(행 클릭·번호 클릭이 따로 놀지 않게). */
  return (
    <ProblemNavGuard confirm={confirmDiscard}>
    <BypassLayout
      renderContent={({ isMobileView }) => {
        // 모바일: 선택 시 디테일 풀스크린, 아니면 목록(단일 컬럼 토글) — 형제 페이지와 동일
        if (isMobileView) {
          return (
            <div
              className="flex flex-1 flex-col"
              style={{ width: 'calc(100% - 28.8px)', height: 'calc(100% - 28.8px)', margin: '14.4px' }}
            >
              {/* 목록은 감추기만 한다 — 언마운트하면 돌아왔을 때 검색 조건·페이지가 사라진다 */}
              <div className={`flex min-h-0 flex-1 flex-col ${selectedId != null ? 'hidden' : ''}`}>
                {renderList()}
              </div>
              {selectedId != null && <div className="flex min-h-0 flex-1 flex-col">{renderDetail()}</div>}
            </div>
          );
        }
        // 데스크톱: 선택 시 좌우 분할(좌 clamp 360~440px), 미선택 시 좌측 full-width
        const split = selectedId != null;
        return (
          <div className="flex h-[calc(100vh-6rem)] w-full gap-4">
            <div
              className={split ? 'h-full shrink-0' : 'size-full min-w-0 flex-1'}
              style={split ? { width: 'clamp(360px, 40%, 440px)', transition: 'width 300ms ease' } : { transition: 'width 300ms ease' }}
            >
              {renderList(false)}
            </div>
            {split && <div className="h-full min-w-0 flex-1">{renderDetail()}</div>}
          </div>
        );
      }}
    />
    {previewIds && (
      <Suspense fallback={null}>
        {/* 문제은행을 보는 자리라 번호가 없다 — 켜면 「01」이 붙어 1번 문항으로 읽힌다.
            구형은 이 스위치의 기본값이 꺼짐이었고 신형은 켜짐이라 **명시해야 같은 얼굴**이다. */}
        <ProblemPreviewV2 isOpen problemIds={previewIds} showNumbers={false} onClose={() => setPreviewIds(null)} />
      </Suspense>
    )}
    {/* 닫으면 언마운트한다 — isOpen 만 내리면 모달 안의 폴링이 계속 돈다(생성 목록과 같은 이유) */}
    {jobsOpen && (
      <AnalysisJobsModal
        isOpen={jobsOpen}
        onClose={() => setJobsOpen(false)}
        onOpenProblem={(pid) => {
          if (pid == null) return;
          // 이동 전에 먼저 묻는다 — 닫고 나서 물으면 취소했을 때 목록만 사라진다
          if (!confirmDiscard()) return;
          setJobsOpen(false); // 배경만 조용히 바뀌지 않게 모달을 닫고 이동한다
          navigate(problemPath(pid, 'analysis')); // 이 목록에서 문제를 여는 이유는 그 문제의 분석을 보려는 것이다
        }}
      />
    )}
    {/* 닫으면 언마운트한다 — isOpen 만 내리면 모달 바깥의 훅(생성 목록 폴링)이 계속 돈다 */}
    {genOpen && (
      <GenerateProblemModal
        isOpen={genOpen}
        onClose={() => { dirtyMapRef.current.generation = false; setGenOpen(false); }}
        onApproved={() => bumpRefresh()}
        onDirtyChange={markDirty('generation')}
        onOpenProblem={(newId) => {
          if (newId == null) return;
          dirtyMapRef.current.generation = false;
          setGenOpen(false); // 배경만 조용히 바뀌지 않게 모달을 닫고 이동한다
          openProblemFront(newId);
        }}
      />
    )}
    {singleOcrOpen && canOpenOcr && <SingleOcrModal
      onClose={() => { dirtyMapRef.current.singleOcr = false; setSingleOcrOpen(false); }}
      onDirtyChange={markDirty('singleOcr')}
      onSaved={(id) => {
        // 이미 열려 있던 은행 문제 편집은 기존 이동 가드로 보호한다.
        dirtyMapRef.current.singleOcr = false;
        setSingleOcrOpen(false); bumpRefresh(); openProblemFront(id);
      }}
    />}
    </ProblemNavGuard>
  );
}

export default ProblemHub;
